import React, { useState, useEffect, useMemo } from 'react';
import { AnalyticsSummary } from '../types/Analytics';
import { analyticsService } from '../services/analyticsService';
import { BarChart3, TrendingUp, TrendingDown, Users, Target, Calendar, Loader2, RefreshCcw, UserPlus } from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import EventsTable from './EventsTable';
import MonetizationChart from './MonetizationChart';

const StatisticsTab: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDays, setSelectedDays] = useState<number>(30);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeEventToggles, setActiveEventToggles] = useState<Record<string, boolean>>({});

  const loadAnalytics = async (isBackground = false) => {
    if (isBackground) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const data = await analyticsService.getAnalyticsSummary(selectedDays);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // If we already have analytics, treat it as a background refresh so we don't unmount the charts
    const isBackground = analytics !== null;
    loadAnalytics(isBackground);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDays, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(k => k + 1);
  };

  // Extract all unique event types
  const allEventTypes = useMemo(() => {
    if (!analytics?.recentEvents) return [];
    const types = new Set(analytics.recentEvents.map((e) => e.eventType));
    return Array.from(types).sort();
  }, [analytics]);

  // Initialize toggles
  useEffect(() => {
    if (Object.keys(activeEventToggles).length === 0 && allEventTypes.length > 0) {
      const initialToggles: Record<string, boolean> = {};
      allEventTypes.forEach((type) => {
        initialToggles[type] = true;
      });
      setActiveEventToggles(initialToggles);
    }
  }, [allEventTypes, activeEventToggles]);

  const EVENT_COLORS: Record<string, string> = {
    level_complete: "#10b981",
    hint_used: "#3b82f6",
    ad_watched: "#f59e0b",
    iap_purchase: "#8b5cf6",
    interstitial_ad: "#ec4899",
    store_review_click: "#06b6d4",
    item_purchase: "#f97316",
    user_created: "#14b8a6",
  };

  const getEventColor = (type: string) => EVENT_COLORS[type] || "#64748b";

  // Compute timeline data exactly like Trivia Dashboard does
  const timelineData = useMemo(() => {
    if (!analytics?.recentEvents) return [];

    const data: Record<string, any> = {};
    const now = new Date();

    // Initialize days
    for (let i = selectedDays - 1; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dateStr = d.toISOString().split("T")[0];
      data[dateStr] = { date: dateStr }; // Use 'date' instead of 'name' so it maps exactly to what XAxis uses
      allEventTypes.forEach(type => {
        data[dateStr][type] = 0;
      });
    }

    // Populate data
    analytics.recentEvents.forEach((e) => {
      const dateStr = new Date(e.timestamp).toISOString().split("T")[0];
      if (data[dateStr] && e.eventType) {
        data[dateStr][e.eventType]++;
      }
    });

    return Object.values(data);
  }, [analytics, selectedDays, allEventTypes]);

  // Total count of each event type in the selected time range
  const eventTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    allEventTypes.forEach(type => { totals[type] = 0; });
    analytics?.recentEvents?.forEach((e) => {
      if (totals[e.eventType] !== undefined) {
        totals[e.eventType]++;
      }
    });
    return totals;
  }, [analytics, allEventTypes]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <Loader2 size={48} className="animate-spin" style={{ margin: '0 auto', color: '#3b82f6' }} />
        <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading statistics...</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <BarChart3 size={64} style={{ margin: '0 auto', color: '#9ca3af' }} />
        <p style={{ marginTop: '1rem', color: '#6b7280' }}>No analytics data available</p>
      </div>
    );
  }


  // Get level analytics sorted by difficulty (lowest success rate = hardest)
  const sortedByDifficulty = Array.from(analytics.levelAnalytics.entries())
    .filter(([_, stats]) => stats.solves + stats.fails > 5) // Only levels with enough attempts
    .sort((a, b) => a[1].successRate - b[1].successRate);

  const hardestLevels = sortedByDifficulty.slice(0, 5);
  const easiestLevels = sortedByDifficulty.slice(-5).reverse();

  return (
    <div style={{ padding: '2rem' }}>
      {/* Page Header — time range + refresh */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ margin: 0 }}>📊 Game Statistics</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {refreshing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#3b82f6', fontSize: '0.875rem' }}>
              <Loader2 size={16} className="animate-spin" />
              Refreshing...
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#6b7280', fontSize: '0.875rem' }}>
            <Calendar size={16} />
            Time range:
          </div>
          <select
            value={selectedDays}
            onChange={(e) => { setSelectedDays(Number(e.target.value)); }}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value={1}>Last day</option>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 3 months</option>
            <option value={365}>Last year</option>
            <option value={3650}>All time</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: (refreshing || loading) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            title="Refresh all data"
          >
            <RefreshCcw size={16} className={(refreshing || loading) ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '8px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Users size={20} />
            <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Players</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.totalPlayers}</div>
        </div>

        <div style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          borderRadius: '8px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Target size={20} />
            <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Attempts</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.totalAttempts.toLocaleString()}</div>
        </div>

        <div style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #14b8a6 0%, #06b6d4 100%)',
          borderRadius: '8px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <UserPlus size={20} />
            <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>New Users</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.newUsers.toLocaleString()}</div>
        </div>

        <div style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
          borderRadius: '8px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Users size={20} />
            <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Returning Users</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.returningUsers.toLocaleString()}</div>
        </div>
      </div>

      {/* Daily Solves Graph */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        marginBottom: '2rem'
      }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Calendar size={20} color="#3b82f6" />
            Daily Activity
          </h3>
        </div>

        {/* Event Toggles */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.5rem" }}>
          {allEventTypes.map(type => (
            <button
              key={type}
              onClick={() => setActiveEventToggles(prev => ({ ...prev, [type]: !prev[type] }))}
              style={{
                padding: "0.4rem 0.8rem",
                borderRadius: "20px",
                border: "none",
                fontSize: "0.85rem",
                cursor: "pointer",
                backgroundColor: activeEventToggles[type] ? getEventColor(type) : "#f1f5f9",
                color: activeEventToggles[type] ? "white" : "#64748b",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: "0.4rem"
              }}
            >
              <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: activeEventToggles[type] ? "white" : getEventColor(type) }}></div>
              {type.replace(/_/g, " ")}
              <span style={{
                marginLeft: "0.2rem",
                fontSize: "0.75rem",
                fontWeight: 700,
                opacity: activeEventToggles[type] ? 0.85 : 0.6,
                background: activeEventToggles[type] ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.08)",
                borderRadius: "10px",
                padding: "0.05rem 0.45rem",
                letterSpacing: "0.02em"
              }}>
                {eventTotals[type] ?? 0}
              </span>
            </button>
          ))}
        </div>

        <div style={{ height: '400px', width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={timelineData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                {allEventTypes.map(type => (
                  <linearGradient key={`grad-${type}`} id={`color-${type}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={getEventColor(type)} stopOpacity={0.1} />
                    <stop offset="95%" stopColor={getEventColor(type)} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: '#6B7280' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(str) => {
                  const date = new Date(str);
                  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#6B7280' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  padding: '12px'
                }}
              />
              <Legend verticalAlign="top" height={36} align="right" iconType="circle" />
              {allEventTypes.map(type => (
                activeEventToggles[type] && (
                  <Area
                    key={type}
                    type="monotone"
                    dataKey={type}
                    name={type.replace(/_/g, " ")}
                    stroke={getEventColor(type)}
                    strokeWidth={3}
                    fillOpacity={1}
                    fill={`url(#color-${type})`}
                  />
                )
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Hardest and Easiest Levels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
        {/* Hardest Levels */}
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            margin: '0 0 1rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#dc2626'
          }}>
            <TrendingDown size={20} />
            Most Challenging Levels
          </h3>
          {hardestLevels.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {hardestLevels.map(([levelId, stats], index) => (
                <div
                  key={`hardest-${levelId}-${index}`}
                  style={{
                    padding: '0.75rem',
                    background: index === 0 ? '#fef2f2' : '#f9fafb',
                    borderRadius: '4px',
                    border: `1px solid ${index === 0 ? '#fecaca' : '#e5e7eb'}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <strong style={{ color: '#374151' }}>Level {levelId}</strong>
                    <span style={{
                      color: 'white',
                      background: '#dc2626',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {stats.successRate.toFixed(1)}%
                    </span>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {stats.solves} solved / {stats.fails} failed ({stats.solves + stats.fails} attempts)
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem 0' }}>
              No data available yet
            </p>
          )}
        </div>

        {/* Easiest Levels */}
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            margin: '0 0 1rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#059669'
          }}>
            <TrendingUp size={20} />
            Easiest Levels
          </h3>
          {easiestLevels.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {easiestLevels.map(([levelId, stats], index) => (
                <div
                  key={`easiest-${levelId}-${index}`}
                  style={{
                    padding: '0.75rem',
                    background: index === 0 ? '#f0fdf4' : '#f9fafb',
                    borderRadius: '4px',
                    border: `1px solid ${index === 0 ? '#bbf7d0' : '#e5e7eb'}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <strong style={{ color: '#374151' }}>Level {levelId}</strong>
                    <span style={{
                      color: 'white',
                      background: '#059669',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {stats.successRate.toFixed(1)}%
                    </span>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {stats.solves} solved / {stats.fails} failed ({stats.solves + stats.fails} attempts)
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem 0' }}>
              No data available yet
            </p>
          )}
        </div>
      </div>

      {/* Monetization Metrics */}
      <div style={{ marginTop: '2rem' }}>
        <MonetizationChart days={selectedDays} refreshKey={refreshKey} />
      </div>

      {/* Recent Events */}
      <div style={{ marginTop: '2rem' }}>
        <EventsTable days={selectedDays} refreshKey={refreshKey} />
      </div>
    </div>
  );
};

export default StatisticsTab;
